(() => {
  const getEnabled = (el) => String(el?.dataset?.jrDraftEnabled || '') === 'true';
  const getEndpoint = (el) => String(el?.dataset?.jrDraftEndpoint || '').trim();
  const getNeedsDraft = (el) => String(el?.dataset?.jrNeedsDraft || '') === 'true';
  const hasNeedsDraftFlag = (el) => {
    if (!el || !el.dataset) return false;
    return Object.prototype.hasOwnProperty.call(el.dataset, 'jrNeedsDraft');
  };

  const isDebug = () => {
    try {
      return new URL(window.location.href).searchParams.get('jr_debug') === '1';
    } catch {
      return false;
    }
  };

  const noteEl = document.getElementById('jr-draft-checkout-note');
  const setNote = (text) => {
    if (!noteEl) return;
    noteEl.hidden = !text;
    noteEl.textContent = text || '';
  };

  const isSameOrigin = (endpoint) => {
    try {
      const url = new URL(endpoint, window.location.origin);
      return url.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const normalizeEndpoint = (endpoint) => {
    try {
      return new URL(endpoint, window.location.origin).toString();
    } catch {
      return endpoint;
    }
  };

  const cartHasConfiguratorItems = (cart) => {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    return items.some((item) => {
      const props = item && item.properties ? item.properties : {};
      return (
        typeof props === 'object' &&
        props &&
        (props['_jr_est_unit_price_cents'] ||
          props['_Config JSON'] ||
          props['_config_json'] ||
          props['_jr_config_json'] ||
          props['_jr_configured'])
      );
    });
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed to load cart (${res.status})`);
    return res.json();
  };

  const createDraftOrder = async (endpoint) => {
    const cart = await fetchCart();
    const res = await fetch(endpoint, {
      method: 'POST',
      // Cross-origin requests are typically blocked by Shopify storefront CSP.
      // Best practice is an App Proxy so this endpoint is same-origin.
      credentials: isSameOrigin(endpoint) ? 'same-origin' : 'omit',
      // Avoid CORS preflight (OPTIONS) on hosts that block it by using a "simple" request.
      // We still send JSON in the body; the server should parse raw bytes as JSON.
      headers: { 'Content-Type': 'text/plain; charset=utf-8' },
      body: JSON.stringify({ cart }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      const message =
        (data && (data.error || data.message)) ||
        (typeof text === 'string' && text.trim() ? text.trim() : '') ||
        `Draft order request failed (${res.status})`;

      const err = new Error(message);
      err.status = res.status;
      err.data = data || text;
      throw err;
    }

    const data = await res.json();
    const invoiceUrl = data && data.invoiceUrl ? String(data.invoiceUrl) : '';
    if (!invoiceUrl) throw new Error('Missing invoiceUrl from server response');
    return invoiceUrl;
  };

  const hideDynamicCheckoutIfNeeded = (needsDraft) => {
    const containers = document.querySelectorAll('.additional-checkout-buttons, .cart__dynamic-checkout-buttons');
    containers.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = Boolean(needsDraft);
    });
  };

  const syncNeedsDraftToButtons = (needsDraft) => {
    document.querySelectorAll('[data-jr-draft-enabled="true"]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.dataset.jrNeedsDraft = needsDraft ? 'true' : 'false';
    });
  };

  const computeNeedsDraftFromDom = () => {
    const nodes = document.querySelectorAll('[data-jr-draft-enabled="true"][data-jr-needs-draft]');
    return Array.from(nodes).some((el) => getNeedsDraft(el));
  };

  const isInCartNotification = (el) => Boolean(el?.closest?.('cart-notification, #cart-notification'));

  const routeToNormalCheckout = (button) => {
    const form = button?.closest?.('form');
    if (form instanceof HTMLFormElement) {
      form.submit();
      return;
    }
    window.location.assign('/checkout');
  };

  const handleCheckout = async (event, button) => {
    const enabled = getEnabled(button);
    const endpoint = normalizeEndpoint(getEndpoint(button));
    if (!enabled || !endpoint) return;

    // IMPORTANT: prevent default synchronously to avoid async submit races.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    try {
      const precomputed = hasNeedsDraftFlag(button) && !isInCartNotification(button);
      let needsDraft = getNeedsDraft(button);

      if (!precomputed) {
        const cart = await fetchCart();
        needsDraft = cartHasConfiguratorItems(cart);
        syncNeedsDraftToButtons(needsDraft);
      }

      hideDynamicCheckoutIfNeeded(needsDraft);
      setNote(needsDraft ? 'Configured items will use draft checkout pricing.' : '');

      if (!needsDraft) {
        routeToNormalCheckout(button);
        return;
      }

      if (button instanceof HTMLButtonElement) button.disabled = true;
      setNote('Creating checkout...');

      const invoiceUrl = await createDraftOrder(endpoint);
      window.location.assign(invoiceUrl);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const extraHint =
        typeof message === 'string' && message.includes('Failed to fetch')
          ? ' (likely blocked by Shopify CSP unless you use a Shopify App Proxy / same-origin endpoint)'
          : '';

      const base = `Checkout failed: ${message}${extraHint}`;
      const help = ' Please try again, or contact us if the problem continues.';
      const debug = isDebug() && e && typeof e === 'object' ? ` (debug: HTTP ${e.status || 'n/a'})` : '';

      setNote(`${base}${help}${debug}`);
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };

  const init = async () => {
    // Initial visibility for dynamic buttons + note.
    const needsDraft = computeNeedsDraftFromDom();
    hideDynamicCheckoutIfNeeded(needsDraft);
    setNote(needsDraft ? 'Configured items will use draft checkout pricing.' : '');

    // Event delegation so we survive cart drawer DOM replacement.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const button = target.closest?.('[data-jr-draft-enabled="true"][data-jr-draft-endpoint]');
        if (!button) return;
        handleCheckout(event, button);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target instanceof HTMLFormElement ? event.target : null;
        if (!form) return;

        const submitter = event.submitter instanceof Element ? event.submitter : null;
        const button =
          submitter?.matches?.('[data-jr-draft-enabled="true"][data-jr-draft-endpoint]')
            ? submitter
            : form.querySelector?.('button[name="checkout"][data-jr-draft-enabled="true"][data-jr-draft-endpoint]');

        if (!button) return;
        handleCheckout(event, button);
      },
      true
    );

    // Keep dynamic checkout buttons and note in sync after cart updates.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const cartData = event?.cartData;
          if (!cartData || typeof cartData !== 'object') return;
          const nextNeedsDraft = cartHasConfiguratorItems(cartData);
          syncNeedsDraftToButtons(nextNeedsDraft);
          hideDynamicCheckoutIfNeeded(nextNeedsDraft);
          setNote(nextNeedsDraft ? 'Configured items will use draft checkout pricing.' : '');
        });
      }
    } catch (e) {
      // ignore
    }
  };

  init();
})();
